import React, { useMemo } from 'react';
import { type Control, Controller, useWatch } from 'react-hook-form';
import { Box, Chip, TextField } from '@mui/material';

interface DiagnosisPointerSelectorProps {
  name: string;
  control: Control<any>; // eslint-disable-line @typescript-eslint/no-explicit-any
  size?: 'small' | 'medium';
  disabled?: boolean;
}

/**
 * Interactive diagnosis pointer selector for CMS-1500 Box 24E
 * Allows clicking diagnosis codes from Box 21 (A-L) instead of typing
 * Shows active diagnoses as clickable chips
 */
export const DiagnosisPointerSelector: React.FC<DiagnosisPointerSelectorProps> = ({
  name,
  control,
  size = 'small',
  disabled = false,
}) => {
  // Watch all diagnosis codes from Box 21
  const diagnosisCodes = useWatch({
    control,
    name: 'diagnosisCodes',
  }) as Record<string, string> | undefined;
  
  // Get available diagnosis letters that have values
  const availableDiagnoses = useMemo(() => {
    if (!diagnosisCodes) return [];
    
    const letters = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L'];
    return letters.filter(letter => diagnosisCodes?.[letter]?.trim());
  }, [diagnosisCodes]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        // Parse current value into array of selected pointers
        const fieldValue = field.value as string | undefined;
        const selectedPointers: string[] = fieldValue
          ? fieldValue.split(',').map((p) => p.trim()).filter(Boolean)
          : [];

        const togglePointer = (pointer: string) => {
          const newPointers = selectedPointers.includes(pointer)
            ? selectedPointers.filter((p) => p !== pointer)
            : [...selectedPointers, pointer].sort();
          
          field.onChange(newPointers.join(','));
        };

        if (availableDiagnoses.length === 0) {
          // If no diagnoses available, show a simple text field
          return (
            <TextField
              {...field}
              size={size}
              placeholder="Enter diagnoses first"
              disabled
              sx={{
                width: '80px',
                '& .MuiInputBase-input': {
                  padding: '2px 4px',
                  fontSize: '12px',
                  textAlign: 'center',
                },
              }}
            />
          );
        }

        // Always show inline chip selector, wrap when needed
        return (
          <Box sx={{ 
            display: 'flex', 
            gap: 0.25, 
            flexWrap: 'wrap', 
            minWidth: '80px',
            maxWidth: '150px',
            alignItems: 'center',
          }}>
            {availableDiagnoses.length === 0 ? (
              <span style={{ 
                fontSize: '11px', 
                color: 'rgba(0, 0, 0, 0.4)',
                fontStyle: 'italic',
              }}>
                No Dx
              </span>
            ) : (
              availableDiagnoses.map(letter => (
                <Chip
                  key={letter}
                  label={letter}
                  size="small"
                  onClick={() => !disabled && togglePointer(letter)}
                  color={selectedPointers.includes(letter) ? 'primary' : 'default'}
                  variant={selectedPointers.includes(letter) ? 'filled' : 'outlined'}
                  disabled={disabled}
                  sx={{
                    height: '18px',
                    fontSize: '10px',
                    cursor: disabled ? 'default' : 'pointer',
                    minWidth: '24px',
                    '& .MuiChip-label': {
                      padding: '0 4px',
                      fontWeight: selectedPointers.includes(letter) ? 600 : 400,
                    },
                  }}
                />
              ))
            )}
          </Box>
        );
      }}
    />
  );
};